import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(8, 'Password must be at least 8 characters')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('request'); // 'request', 'success', 'reset'
  const [resetToken, setResetToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const { toast } = useToast();

  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors },
    reset: resetForgotForm
  } = useForm({
    resolver: zodResolver(forgotPasswordSchema)
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
    reset: resetResetForm
  } = useForm({
    resolver: zodResolver(resetPasswordSchema)
  });

  const handleClose = () => {
    setStep('request');
    setResetToken('');
    setUserEmail('');
    resetForgotForm();
    resetResetForm();
    onClose();
  };

  const onSubmitForgotPassword = async (data) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (response.ok) {
        setUserEmail(data.email);
        setStep('success');
        toast({
          title: "Reset link sent!",
          description: "Please check your email for the password reset link.",
        });
      } else {
        toast({
          title: "Error",
          description: result.detail || "Failed to send reset email. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitResetPassword = async (data) => {
    setIsLoading(true);
    
    try {
      console.log('=== PASSWORD RESET DEBUG ===');
      console.log('Reset token:', resetToken);
      console.log('Token type:', typeof resetToken);
      console.log('Token length:', resetToken ? resetToken.length : 0);
      console.log('New password length:', data.password.length);
      console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);
      
      if (!resetToken || resetToken.trim() === '') {
        console.error('ERROR: Reset token is empty or undefined!');
        toast({
          title: "Error",
          description: "Reset token is missing. Please click the link in your email again.",
          variant: "destructive"
        });
        return;
      }
      
      const payload = { 
        token: resetToken.trim(), 
        new_password: data.password 
      };
      console.log('Request payload:', JSON.stringify(payload));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      
      console.log('Reset password response status:', response.status);

      const result = await response.json();
      console.log('Response data:', result);

      if (response.ok) {
        // Clear the token from URL after successful reset
        window.history.replaceState({}, document.title, window.location.pathname);
        
        toast({
          title: "Password reset successful!",
          description: "You can now log in with your new password.",
        });
        handleClose();
      } else {
        console.error('Password reset failed:', result);
        toast({
          title: "Error",
          description: result.detail || "Failed to reset password. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset from URL token (when user clicks email link)
  React.useEffect(() => {
    // Always try to get token from URL when modal opens
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('reset_token');
    
    if (token && token.trim() !== '') {
      console.log('Token detected in URL:', token);
      console.log('Token length:', token.length);
      
      // Only set token and switch step if we don't already have one
      if (!resetToken || resetToken !== token) {
        setResetToken(token);
        setStep('reset');
        console.log('Token set and modal switched to reset step');
      }
    } else {
      console.log('No valid token found in URL');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {step === 'request' && (
          <>
            <DialogHeader>
              <DialogTitle>Reset your password</DialogTitle>
              <DialogDescription>
                Enter your email address and we'll send you a link to reset your password.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmitForgot(onSubmitForgotPassword)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10"
                    {...registerForgot('email')}
                  />
                </div>
                {forgotErrors.email && (
                  <p className="text-sm text-red-600">{forgotErrors.email.message}</p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === 'success' && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <DialogTitle className="text-center">Check your email</DialogTitle>
              <DialogDescription className="text-center">
                We've sent a password reset link to <strong>{userEmail}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('request')}
                  className="flex-1"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={handleClose}
                  className="flex-1"
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'reset' && (
          <>
            <DialogHeader>
              <DialogTitle>Create new password</DialogTitle>
              <DialogDescription>
                Enter your new password below.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmitReset(onSubmitResetPassword)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  {...registerReset('password')}
                />
                {resetErrors.password && (
                  <p className="text-sm text-red-600">{resetErrors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  {...registerReset('confirmPassword')}
                />
                {resetErrors.confirmPassword && (
                  <p className="text-sm text-red-600">{resetErrors.confirmPassword.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset password'
                )}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;